import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { load } from '../../src/web/store/modules/app';
import {
  load as loadPage,
  getId as getPageId,
  isEditingPage,
  isEditingLayout,
} from '../../src/web/store/modules/pageBuilder';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import SiteEditForm from '../components/SiteEditForm';

export const GET_DATA = gql`
  query GetCurrentSiteEditData {
    currentCompany {
      id
      ...SiteEditFormCompany
    }
    currentSite {
      id
      ...SiteEditFormSite
    }
  }

  ${SiteEditForm.fragments.company}
  ${SiteEditForm.fragments.site}
`;

export const UPDATE_CURRENT_SITE = gql`
  mutation CurrentSiteEditCurrentSiteUpdate($currentSite: SiteInput!) {
    currentSiteUpdate(currentSite: $currentSite) {
      currentSite {
        id
        ...SiteEditFormSite
      }
      userErrors {
        code
        message
      }
    }
  }

  ${SiteEditForm.fragments.site}
`;

export const UPDATE_CURRENT_SITE_WITH_MEDIUM = gql`
  mutation CurrentSiteEditCurrentSiteUpdate($currentSite: SiteInput!, $mediumId: ID!, $input: MediumInput!) {
    mediumUpdate(id: $mediumId, input: $input) {
      medium {
        id
        ...SiteEditFormMedium
      }
      userErrors {
        code
        message
      }
    }
    currentSiteUpdate(currentSite: $currentSite) {
      currentSite {
        id
        ...SiteEditFormSite
      }
      userErrors {
        code
        message
      }
    }
  }

  ${SiteEditForm.fragments.site}
  ${SiteEditForm.fragments.medium}
`;

const propTypes = {
  id: PropTypes.string.isRequired,
  isPage: PropTypes.bool.isRequired,
  isLayout: PropTypes.bool.isRequired,
  loadSite: PropTypes.func.isRequired,
  loadBuilderPage: PropTypes.func.isRequired,
  loadBuilderLayout: PropTypes.func.isRequired,
};

const defaultProps = {};

const CurrentSiteEdit = ({
  id,
  isPage,
  isLayout,
  loadSite,
  loadBuilderPage,
  loadBuilderLayout,
  ...rest
}) => {
  const { data: currentSiteData } = useQuery(GET_DATA, { fetchPolicy: 'network-only' });
  const [updateCurrentSite, { data, error, loading }] = useMutation(UPDATE_CURRENT_SITE);
  const [updateCurrentSiteWithMedium,
    { data: dataWithMedium, error: errorWithMedium },
  ] = useMutation(UPDATE_CURRENT_SITE_WITH_MEDIUM);

  let submitErrors;
  let submitted;

  const onSubmit = async (site) => {
    let currentSite = site;

    if (site.socialMediaPlatforms) {
      const formattedPlatforms = Object.entries(site.socialMediaPlatforms).map(
        ([provider, { link = '', enabled = false }]) => ({ provider, link, enabled }),
      );
      currentSite = {
        ...site,
        socialMediaPlatforms: formattedPlatforms,
      };
    }

    if (site.theme.logo.mediumId) {
      const mediumInput = {
        mediumId: site.theme.logo.mediumId,
        input: {
          defaultMetadata: {
            altText: site.theme.logo.alt,
            locale: 'en-us',
            title: site.theme.logo.title,
          },
        },
      };

      updateCurrentSiteWithMedium({ variables: { currentSite, ...mediumInput } });
    } else {
      updateCurrentSite({ variables: { currentSite } });
    }


    // dispatch Redux actions to load site, page/layout
    loadSite();
    if (isPage) {
      loadBuilderPage(id);
    } else if (isLayout) {
      loadBuilderLayout(id);
    }
  };

  if (data) {
    const { currentSite: updatedCurrentSite, userErrors } = data.currentSiteUpdate;

    if (updatedCurrentSite) {
      submitted = true;
    }

    if (userErrors.length > 0) {
      submitErrors = userErrors;
      submitted = false;
    }
  }

  if (error) {
    const message = { code: 0, message: 'Could not update site settings.' };

    submitErrors = submitErrors ? [...submitErrors, message] : [message];
    submitted = false;
  }

  if (dataWithMedium) {
    const { currentSite: updatedCurrentSite, userErrors } = dataWithMedium.currentSiteUpdate;

    if (updatedCurrentSite) {
      submitted = true;
    }

    if (userErrors.length > 0) {
      submitErrors = userErrors;
      submitted = false;
    }
  }

  if (errorWithMedium) {
    const message = { code: 0, message: 'Could not update site settings.' };

    submitErrors = submitErrors ? [...submitErrors, message] : [message];
    submitted = false;
  }

  if (!currentSiteData) {
    return null;
  }

  return (
    <SiteEditForm
      {...rest}
      company={currentSiteData.currentCompany}
      onSubmit={onSubmit}
      site={currentSiteData.currentSite}
      submitted={submitted}
      submitting={loading}
      submitErrors={submitErrors}
    />
  );
};

CurrentSiteEdit.propTypes = propTypes;
CurrentSiteEdit.defaultProps = defaultProps;

/*
 * On site save, dispatch the Redux action to load the app to ensure
 * social media, logos, etc are updated in the builder.
 * When the CurrentPageContext is updated to pull data from Apollo GraphQL/V2 API
 * instead Redux/V1 API we can get rid of this.
 */
const mapStateToProps = state => ({
  id: getPageId(state),
  isPage: isEditingPage(state),
  isLayout: isEditingLayout(state),
});

const mapDispatchToProps = dispatch => ({
  loadSite: () => dispatch(load(true)),
  loadBuilderPage: id => dispatch(loadPage(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CurrentSiteEdit);
